/**
 * =============================================================================
 * IMAGE LIGHTBOX COMPONENT
 * =============================================================================
 *
 * Full-viewport image lightbox for the storefront question detail page.
 * Replaces raw target="_blank" image links with an in-page viewer.
 *
 * Features:
 * - Dark overlay with centered image
 * - Previous/Next navigation (arrows + keyboard)
 * - Close on ESC, click-outside, or X button
 * - Image counter ("2 of 5")
 * - Body scroll lock while open
 * - Accessible: role="dialog", aria-modal, keyboard navigation
 * - Respects prefers-reduced-motion
 *
 * =============================================================================
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useStyleInjection } from '../../hooks';
import { BREAKPOINTS } from '../../tokens';

// =============================================================================
// TYPES
// =============================================================================

export interface LightboxImage {
  url: string;
  /** Intrinsic dimensions (ADR-022 §8) — reserve space so the full image load
   *  does not shift layout. Both present or neither (never one alone). */
  width?: number;
  height?: number;
}

export interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
  /**
   * Accessible noun for the generic image labels, e.g. "Question image" /
   * "Reply image". Public labels are ALWAYS positional/generic — NEVER derived
   * from an uploaded filename (ADR-022 §6.1/§9, G9). Defaults to "Image".
   */
  labelNoun?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ImageLightbox({
  images,
  initialIndex,
  onClose,
  labelNoun = 'Image',
}: ImageLightboxProps): React.ReactElement {
  useStyleInjection(lightboxStyles, 'fmp-lightbox-styles');

  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Defensive against an out-of-range index: if the `images` array shrinks while
  // the lightbox is open (a future refetch/poll/optimistic-update on the source —
  // exactly what the widget's data hook is built for), `images[currentIndex]`
  // would be `undefined` and the render would throw. Fall back to the last valid
  // image (the parent never renders this with an empty list), and the clamp
  // effect below realigns `currentIndex` so the counter/nav stay correct.
  const currentImage = images[currentIndex] ?? images[images.length - 1];
  const hasMultiple = images.length > 1;

  useEffect(() => {
    setCurrentIndex((prev) => (prev > images.length - 1 ? Math.max(images.length - 1, 0) : prev));
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Save previous focus and lock body scroll on mount
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    // Snapshot the HOST's own lock (a theme may already hold one) and restore it.
    const hostOverflow = document.body.style.getPropertyValue('overflow');
    const hostPriority = document.body.style.getPropertyPriority('overflow');
    document.body.style.setProperty('overflow', 'hidden');
    dialogRef.current?.focus();

    return () => {
      if (hostOverflow) document.body.style.setProperty('overflow', hostOverflow, hostPriority);
      else document.body.style.removeProperty('overflow');
      const previous = previousFocusRef.current; // tabindex="-1" is a valid successor
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  // Keyboard navigation + focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          if (hasMultiple) goPrev();
          break;
        case 'ArrowRight':
          if (hasMultiple) goNext();
          break;
        case 'Tab': {
          // Focus trap: cycle focus within the dialog
          const dialog = dialogRef.current;
          if (!dialog) break;
          const focusable = dialog.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length === 0) break;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first || document.activeElement === dialog) {
              e.preventDefault();
              last.focus();
            }
          } else {
            if (document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goNext, goPrev, hasMultiple]);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <div
      ref={dialogRef}
      className="fmp-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Image viewer: ${labelNoun} ${currentIndex + 1} of ${images.length}`}
      tabIndex={-1}
    >
      <div className="fmp-lightbox__backdrop" onClick={handleBackdropClick}>
        {/* Close button */}
        <button
          type="button"
          className="fmp-lightbox__close"
          onClick={onClose}
          aria-label="Close image viewer"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Previous button */}
        {hasMultiple && (
          <button
            type="button"
            className="fmp-lightbox__nav fmp-lightbox__nav--prev"
            onClick={goPrev}
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {/* Image. Generic positional alt only (never a filename — §6.1/§9).
            width/height reserve the box so the full-size load doesn't shift
            layout; decoding=async keeps the main thread free. */}
        <img
          src={currentImage.url}
          alt={`${labelNoun} ${currentIndex + 1} of ${images.length}`}
          className="fmp-lightbox__image"
          decoding="async"
          {...(currentImage.width && currentImage.height
            ? {
                width: currentImage.width,
                height: currentImage.height,
                style: { aspectRatio: `${currentImage.width} / ${currentImage.height}` },
              }
            : {})}
        />

        {/* Next button */}
        {hasMultiple && (
          <button
            type="button"
            className="fmp-lightbox__nav fmp-lightbox__nav--next"
            onClick={goNext}
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}

        {/* Counter */}
        {hasMultiple && (
          <div className="fmp-lightbox__counter" aria-live="polite">
            {currentIndex + 1} of {images.length}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const lightboxStyles = `
  :where(.fmp-widget-scope) .fmp-lightbox {
    position: fixed;
    inset: 0;
    z-index: var(--fmp-z-modal);
  }

  :where(.fmp-widget-scope) .fmp-lightbox:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__backdrop {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: var(--fmp-color-overlay);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__image {
    max-width: var(--fmp-layout-lightbox-max-inline);
    max-height: var(--fmp-layout-lightbox-max-block);
    object-fit: contain;
    border-radius: var(--fmp-radius-sm);
    user-select: none;
  }

  :where(.fmp-widget-scope) .fmp-lightbox__close {
    position: absolute;
    top: var(--fmp-size-16);
    right: var(--fmp-size-16);
    width: var(--fmp-size-44);
    height: var(--fmp-size-44);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--fmp-color-overlay-control);
    color: var(--fmp-color-overlay-text);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
    z-index: var(--fmp-z-raised);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__close:hover {
    background: var(--fmp-color-overlay-control-hover);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__close:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-overlay-text);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__close svg {
    width: var(--fmp-size-20);
    height: var(--fmp-size-20);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: var(--fmp-size-48);
    height: var(--fmp-size-48);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: var(--fmp-color-overlay-control);
    color: var(--fmp-color-overlay-text);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard);
    z-index: var(--fmp-z-raised);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav:hover {
    background: var(--fmp-color-overlay-control-hover);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-overlay-text);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav svg {
    width: var(--fmp-size-24);
    height: var(--fmp-size-24);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav--prev {
    left: var(--fmp-size-16);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__nav--next {
    right: var(--fmp-size-16);
  }

  :where(.fmp-widget-scope) .fmp-lightbox__counter {
    position: absolute;
    bottom: var(--fmp-size-20);
    left: 50%;
    transform: translateX(-50%);
    padding: var(--fmp-size-6) var(--fmp-space-lg);
    border-radius: var(--fmp-radius-pill);
    background: var(--fmp-color-overlay-caption);
    color: var(--fmp-color-overlay-text);
    font-size: var(--fmp-font-size-sm);
    font-weight: var(--fmp-font-weight-semibold);
    white-space: nowrap;
    user-select: none;
  }

  @media screen and (max-width: ${BREAKPOINTS.viewport.smallMax}) {
    :where(.fmp-widget-scope) .fmp-lightbox__nav {
      width: var(--fmp-size-40);
      height: var(--fmp-size-40);
    }

    :where(.fmp-widget-scope) .fmp-lightbox__nav--prev {
      left: var(--fmp-size-8);
    }

    :where(.fmp-widget-scope) .fmp-lightbox__nav--next {
      right: var(--fmp-size-8);
    }

    :where(.fmp-widget-scope) .fmp-lightbox__close {
      top: var(--fmp-size-8);
      right: var(--fmp-size-8);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-lightbox__close,
    :where(.fmp-widget-scope) .fmp-lightbox__nav {
      transition: none;
    }
  }
`;
